<template>
  <div class="dashboard-view">
    <div class="card border-0">
      <div class="card-body border-0 pt-0 pb-3 pl-0 pr-0" style="background-color: #E3E8EE">
        <div class="main-wrapper" v-if="inventory">
          <div class="nested-grid">
            <div class="card inventory-info-card" v-animate-fade="0.2">
              <div class="card-header d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <h5 class="mb-0 d-inline-block">{{ $t('Inventory Information') }}</h5>
                  <span class="ml-2 badge badge-secondary" v-if="inventory && inventory.is_draft">
                    {{ $t('Draft') }}
                  </span>
                </div>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_inventoryitem'"
                          @click="onEditClick(inventory)">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-sm-6 border-right">
                    <table class="table table-sm mb-0">
                      <tr>
                        <td class="font-weight-bold border-top-0">{{ $t('Receipt / Item') }}</td>
                        <td class="border-top-0">
                          <router-link v-if="$hasPermission('view_receipt')"
                                       :to="{name: 'receipt.view', params: {id: inventory.receipt_uuid}}">
                            {{ inventory.receipt.receipt_no }}
                          </router-link>
                          <p class="m-0" v-else> {{ inventory.receipt.receipt_no }}</p>
                          / {{ inventory.item_no }}
                          <grouped-item-indicator :inventory="inventory"/>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Sale') }}</td>
                        <td>
                          <div v-for="sale in itemSales" :key="`sale-route-${sale.uuid}`">
                            <router-link v-if="!enableContinuousAuction"
                                         :to="{name: 'sales.view', params: {id: sale.uuid}}">
                              {{ sale.sale_no }}
                            </router-link>
                            <span v-else>
                              {{ sale.sale_no }}
                            </span>
                            <live-sale-ref v-if="sale.live_reference" :href="sale.live_url"
                                           :label="sale.live_reference"/>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="enableInventoryQuantity">
                        <td class="font-weight-bold">{{ $t('Quantity') }}</td>
                        <td>{{ inventory.quantity }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Status') }}</td>
                        <td>
                          <inventory-status :inventory="inventory"/>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Lot Number') }}</td>
                        <td>{{ getLatestLotNumber }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Receive Date') }}</td>
                        <td>{{ inventory.receive_date | toDate }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Collected') }}</td>
                        <td>
                          <span v-if="inventory.collected" class="badge badge-success">{{ $t('Yes') }}</span>
                          <span v-else class="badge badge-danger">{{ $t('No') }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Collected Status') }}</td>
                        <td>
                          <template v-if="inventory.collected_status && inventory.collected_status.length > 0">
                            <span v-as-status="statusObj.status" v-for="statusObj in inventory.collected_status"
                                  :key="`inventory-collected-status-${inventory.uuid}-${statusObj.status}`"
                                  class="mr-2">
                              {{ statusObj.status | removeUnderscores | toUpperCaseEveryWord }} ({{
                                statusObj.quantity
                              }})
                            </span>
                          </template>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Specialists') }}</td>
                        <td>
                          <ul v-if="inventory.specialists" style="list-style: none;padding-left: 0;">
                            <li v-for="specialist in inventory.specialists" :key="specialist.uuid">
                              <span>
                              {{ specialist.display_name }} - {{ specialist.email }}
                              </span>
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Cataloguing Lifecycle') }}</td>
                        <td>
                          <span v-if="inventory.cataloguing_lifecycle">
                            <cataloguing-lifecycle-display-widget :inventory="inventory" />
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Photography Lifecycle') }}</td>
                        <td>
                          <span v-if="inventory.photography_lifecycle">
                            <photography-lifecycle-display-widget :inventory="inventory" />
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Branch') }}</td>
                        <td>
                          <template v-if="inventory.branch">
                            <router-link v-if="$hasPermission('setup')"
                                         :to="{name: 'branches', params: {branch_uuid: inventory.branch_uuid}}">
                              {{ inventory.branch.name }}
                            </router-link>
                            <span v-else>{{ inventory.branch.name }}</span>
                          </template>
                        </td>
                      </tr>
                      <tr v-if="!inventory.is_grouped_item">
                        <td class="font-weight-bold">{{ $t('Department') }}</td>
                        <td>
                          <template v-if="inventory.department">
                            <router-link v-if="$hasPermission('setup')"
                                         :to="{name: 'departments', params: {department_uuid: inventory.department_uuid}}">
                              {{ inventory.department.name | masterLanguage }}
                            </router-link>
                            <span v-else>{{ inventory.department.name | masterLanguage }}</span>
                          </template>
                        </td>
                      </tr>
                      <tr v-if="!isSalesTaxEnabled">
                        <td class="font-weight-bold">{{ $t('Tax Status') }}</td>
                        <td>{{ taxStatusName }}</td>
                      </tr>
                      <tr v-if="!inventory.is_grouped_item">
                        <td class="font-weight-bold">{{ $t('Category') }}</td>
                        <td><span v-if="inventory.category">{{ inventory.category.name | masterLanguage }}</span></td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Item Location') }}</td>
                        <td><span v-if="inventory.item_location_uuid">{{ itemLocationComputedName }}</span></td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Transfer Info') }}</td>
                        <td>
                          <transfer-note-short-info :item="inventory"/>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Estimate') }}</td>
                        <td>
                          <template v-if="inventory.low || inventory.high">
                            {{ inventory.low | asCurrency }} - {{ inventory.high | asCurrency }}
                          </template>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Start Price') }}</td>
                        <td>
                          {{ inventory.start_price | asCurrency }}
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Reserve') }}</td>
                        <td>
                          {{ inventory.reserve | asCurrency }}
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Reserve Type') }}</td>
                        <td>
                          {{ displayReserveType }}
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">{{ $t('Collection Information') }}</td>
                        <td>
                          {{ inventory.collection_info_full }}
                        </td>
                      </tr>
                    </table>
                  </div>
                  <div class="col-sm-6" v-if="inventory.dynamic_fields">
                    <dynamic-fields-info-table :dynamic-fields="inventory.dynamic_fields"
                                               :dynamic-fields-template="sortedDynamicFieldsTemplate"/>
                  </div>
                </div>
              </div>
            </div>

            <div class="card movement-history-card" v-animate-fade="0.25"
                 v-if="inventory.movement_history_count > 0">
              <div class="card-header d-flex align-items-center">
                <h5 class="mb-0">{{ $t('Movement History') }}</h5>
                <router-link class="ml-auto"
                             :to="{name: 'inventory.movementHistory', params: {id: inventory.uuid}}">
                  {{ $t('View all') }}
                </router-link>
              </div>
              <div class="card-body">
                <movement-history-list :entries="inventory.movement_histories || []" :limit="3" :empty-height="3"/>
              </div>
            </div>

            <div class="card misc-items-card" v-animate-fade="0.3">
              <div class="card-header d-flex align-items-center">
                <h5 class="mb-0">{{ $t('Invoice Misc Charges') }}</h5>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_inventoryitem'"
                          @click="onAddNewMiscItemClick(inventory)">
                  <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Add New') }}
                </b-button>
              </div>
              <div class="card-body">
                <inventory-misc-charges-table
                  v-if="inventory.inventory_miscellaneous_items && inventory.inventory_miscellaneous_items.length > 0"
                  :misc-items="inventory.inventory_miscellaneous_items"/>
                <no-data-available v-else height="3"/>
              </div>
            </div>

            <div class="card customer-card" v-animate-fade="0.6">
              <div class="card-header d-flex align-items-center">
                <h5 class="mb-0 d-inline-block">{{ $t('Vendor Information') }}</h5>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_customer'"
                          @click="onEditCustomerClick({source: SOURCE_INVENTORY_DASHBOARD, object: inventory.customer})">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body">
                <customer-info-table
                  :exclude-fields="['email2','phone2','tags','acquisition_source']"
                  :customer="inventory.customer"/>
              </div>
            </div>
            <div class="flex-column align-items-stretch d-flex" v-animate-fade="0.8">
              <div class="card mb-2" style="flex: 1" v-if="inventory.grouped_inventories_count > 0">
                <div class="card-header">
                  <h5 class="mb-0">{{ $t("Grouped In") }}</h5>
                </div>
                <div class="card-body overflow-auto pt-0" style="max-height: 13em">
                  <table class="table table-sm mb-0">
                    <thead>
                    <tr>
                      <td class="border-top-0">{{ $t('Stock No') }}</td>
                      <td class="border-top-0">{{ $t('Title') }}</td>
                      <td class="border-top-0">{{ $t('Quantity') }}</td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr :key="`grouped-item-row-${groupedItem.uuid}`"
                        v-for="(groupedItem) in inventory.grouped_inventories">
                      <td>
                        <linked-stock-no :inventory-uuid="groupedItem.grouped_inventory.uuid"
                                         :receipt-uuid="groupedItem.grouped_inventory.receipt_uuid"
                                         :stock-no="groupedItem.grouped_inventory.stock_no"/>
                      </td>
                      <td>
                        {{ groupedItem.grouped_inventory.short_description | striptags | truncate(20) }}
                      </td>
                      <td>
                        {{ groupedItem.quantity }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <NoteList style="flex: 1" class="notes-card"
                        :object-uuid="inventory.uuid"
                        truncate
                        change-permission="change_inventoryitem"
                        delete-permission="delete_inventoryitem"
                        plural-object-name="inventories"
                        object-name="inventory"/>
            </div>
          </div>
          <div class="card" v-animate-fade="0.4">
            <div class="card-body">
              <images-view :inventory-uuid="inventory.uuid" :disabled="shouldDisable"
                           change-permission="change_inventoryitem"
                           delete-permission="delete_inventoryitem"
                           :enable-image-edit="$hasPermission('change_inventoryitem')"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import {createNamespacedHelpers} from "vuex";
  import {SOURCE_INVENTORY_DASHBOARD} from "../../../constants";
  import ImagesView from "../images/ImagesView";
  import NoteList from "../../../notes/NotesList";
  import CustomerInfoTable from "@/modules/customer/view/dashboard/CustomerInfoTable";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import DynamicFieldsInfoTable from "@/modules/inventory/view/dashboard/DynamicFieldsInfoTable";
  import i18nHelper from "../../../../core/services/i18n.service";
  import helperService from "@/core/services/helper.service";
  import InventoryStatus from "@/modules/inventory/components/InventoryStatus";
  import GroupedItemIndicator from "@/modules/inventory/GroupedItemIndicator";
  import LinkedStockNo from "@/modules/inventory/LinkedStockNo";
  import InventoryMiscChargesTable
    from "@/modules/inventory/view/dashboard/inventory-misc-charges/InventoryMiscChargesTable";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import TransferNoteShortInfo from "@/modules/inventory/components/TransferNoteShortInfo";
  import CataloguingLifecycleDisplayWidget from "@/core/widgets/CataloguingLifecycleDisplayWidget.vue";
  import PhotographyLifecycleDisplayWidget from "@/core/widgets/PhotographyLifecycleDisplayWidget.vue";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";
  import MovementHistoryList from "@/modules/inventory/view/movement-history/MovementHistoryList";

  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  export default {
    name: "DashboardView",
    components: {
      PhotographyLifecycleDisplayWidget,
      CataloguingLifecycleDisplayWidget,
      TransferNoteShortInfo,
      NoDataAvailable,
      InventoryMiscChargesTable,
      LinkedStockNo,
      GroupedItemIndicator,
      InventoryStatus, DynamicFieldsInfoTable, CustomerInfoTable, NoteList, ImagesView, LiveSaleRef,
      MovementHistoryList
    },
    data: () => {
      return {
        SOURCE_INVENTORY_DASHBOARD: SOURCE_INVENTORY_DASHBOARD,
        masterLanguage: i18nHelper.getMasterLanguage(),
      };
    },
    computed: {
      taxStatusName() {
        if (this.inventory && this.taxStatuses) {
          const taxStatus = this.taxStatuses.find(ts => ts.uuid === this.inventory.tax_status_uuid);
          return taxStatus ? taxStatus.name : '';
        }
        return '';
      },
      ...mapState({
        inventory: state => state.view.currentInventory
      }),
      ...globalMapState({
        taxStatuses: state => state.taxStatuses,
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        enableContinuousAuction: state => state.enableContinuousAuction,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        reserveTypes: state => state.reserveTypes,
        disableItemEditOnSold: state => state.disableItemEditOnSold,
        itemLocations: state => state.itemLocations,
      }),
      itemSales() {
        if (!this.inventory || !this.inventory.reservations || this.inventory.reservations.length === 0) {
          return [];
        }
        const saleArr = [];
        for (let reservation of this.inventory.reservations) {
          if (!saleArr.find(s => s.uuid === reservation.auction_uuid)) {
            saleArr.push(reservation.auction);
          }
        }
        return saleArr;
      },
      itemLocationComputedName() {
        if (!this.inventory || !this.inventory.item_location_uuid) {
          return '';
        }
        if (this.itemLocations && this.itemLocations.length) {
          const node = ItemLocationService.findNode(this.itemLocations, this.inventory.item_location_uuid, 'uuid', 'children');
          if (node) return node.computed_name;
        }
        return this.inventory.item_location ? this.inventory.item_location.name : '';
      },
      sortedDynamicFieldsTemplate() {
        return helperService.sortDynamicFieldsTemplate(helperService.calculateDynamicFieldsTemplateFromFields(this.inventory.dynamic_fields));
      },
      displayReserveType() {
        if (!this.inventory || !this.inventory.reserve_type) {
          return ''
        }
        const tmpType = this.reserveTypes.find(r => r.value === this.inventory.reserve_type)
        return tmpType ? tmpType.key : ""
      },
      getLatestLotNumber() {
        if (!this.inventory || !this.inventory.latest_reservation)
          return '';
        return this.inventory.latest_reservation.lot_no;
      },
      shouldDisable() {
        if (!this.disableItemEditOnSold || !this.inventory) {
          return false;
        }

        if (this.inventory.reservations && this.inventory.reservations.filter(r => r.status === 'sold' && r.auction && r.auction.status === 'completed').length > 0) {
          return true
        }

        if (this.inventory.financial_items && this.inventory.financial_items.filter(fi => fi.invoice && fi.invoice.status === 'paid').length > 0) {
          return true
        }

        return false
      }
    },
    methods: {
      ...mapActions(['showModal', 'getModalObject', 'showInventoryMiscChargeModal']),
      ...customerMapActions({
        showCustomerModal: 'showModal',
        getCustomerModalObject: 'getModalObject',
      }),
      onEditClick(item) {
        this.showModal();
        this.getModalObject({uuid: item.uuid, source: SOURCE_INVENTORY_DASHBOARD});
      },
      onEditCustomerClick() {
        this.showCustomerModal();
        this.getCustomerModalObject({uuid: this.inventory.customer.uuid, source: SOURCE_INVENTORY_DASHBOARD});
      },
      onAddNewMiscItemClick() {
        this.showInventoryMiscChargeModal({
          source: SOURCE_INVENTORY_DASHBOARD,
          object: {inventory_uuid: this.inventory.uuid}
        })
      },
    }
  }

</script>

<style lang="scss" scoped>
  @import "./../../scss/variables";

  .main-wrapper {

    .dynamic-fields-info-table {
      max-height: 40em;
      overflow-y: auto;
    }

    display: grid;
    grid-gap: 1em;
    grid-template-columns: 2fr 1fr;

    .nested-grid {
      display: grid;
      grid-gap: 1em;
      grid-template-columns: 1fr 1fr;

      .inventory-info-card {
        grid-column: 1/3;
      }

      .movement-history-card {
        grid-column: 1;
      }

      .misc-items-card {
        grid-column: 1/3;
      }
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "xl")) {
    .main-wrapper {
      grid-template-columns: 1fr;
    }
  }
</style>
